import clsx from 'clsx';
import { FunctionComponent, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { CropHint } from '@vestico/model';

import { useElementSize } from '../hooks';
import { useCropHintStyles } from '../hooks/useCropHintStyles';
import style from './ImageDisplayBlur.module.css';

export interface ImageDisplayBlurProps {
  coverURL?: string;
  cropHint?: CropHint;
}

export const ImageDisplayBlur: FunctionComponent<ImageDisplayBlurProps> = ({ coverURL, cropHint }) => {
  const [loadedThumbImageURL, setLoadedThumbImageURL] = useState<undefined | string>(undefined);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const coverRef = useRef<HTMLDivElement | null>(null);
  const [mediaAspectRatio, setMediaAspectRatio] = useState<number | undefined>(undefined);
  const containerDimensions = useElementSize(rootRef);

  const { css: thumbCSS } = useCropHintStyles({ cropHint, mediaAspectRatio, containerDimensions });

  const onThumbLoad = useCallback(
    (e: Event) => {
      const { width, height } = e.currentTarget as HTMLImageElement;
      if (width && height) {
        setMediaAspectRatio(width / height);
      }
      setLoadedThumbImageURL(coverURL);
    },
    [coverURL]
  );

  useLayoutEffect(() => {
    const hasAlreadyLoadedThumb = loadedThumbImageURL === coverURL;

    let thumbImgElement: HTMLImageElement | undefined;

    if (coverURL && !hasAlreadyLoadedThumb) {
      setLoadedThumbImageURL(undefined);
      setMediaAspectRatio(undefined);
      const img = new Image();
      thumbImgElement = img;
      img.src = coverURL;
      img.decoding = 'async';
      img.onload = onThumbLoad;
    }
    // Reset `onload` when unmounting the component
    return () => {
      if (thumbImgElement) {
        thumbImgElement.onload = null;
      }
    };
  }, [coverURL, loadedThumbImageURL, onThumbLoad]);

  return (
    <div className={clsx(style.root)} ref={rootRef}>
      <div className={clsx(style.thumbImage)} ref={coverRef}>
        {/* skipcq JS-W1015 */}
        <img src={loadedThumbImageURL} className={clsx(style.image)} draggable={false} alt="cover" style={thumbCSS} />
        <div className={clsx(style.blurBackground)} />
      </div>
    </div>
  );
};
